import { signalRNotificationsApi } from '../../apiCalls'
import {
	NotificationEntityType,
	NotificationGroupEntity,
	NotificationMessage,
} from '../../model/signalRNotifications'

// The notifications API is a wrapper around the SignalR API that allows for easier management of connections and groups.
// https://learn.microsoft.com/en-us/azure/azure-signalr/signalr-reference-data-plane-rest-api

let connectionId: string | null = null
const groups: string[] = []

function randomId(): string {
	return Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
}

export function composeGroupName(
	groupEntity: NotificationGroupEntity,
	groupValue?: string,
	entityType?: NotificationEntityType,
	entityValue?: string,
): string {
	let groupName = `${groupEntity}`
	if (groupValue) {
		groupName += `:${groupValue}`
	}
	if (entityType && entityValue) {
		groupName += `:${entityType}:${entityValue}`
	}
	return groupName
}

/**
 * Adds the current connection to a specified group.
 * @param {string} group - The group to add the connection to.
 * @returns bool
 */
export async function addConnectionToGroup(group: string): Promise<boolean> {
	if (!connectionId) {
		return false
	}

	const url = `notifications/groups/${group}/connections/${connectionId}`
	const response = await signalRNotificationsApi(url, 'PUT')

	if (!response.ok) {
		return false
	}

	groups.push(group)

	return true
}

/**
 * Removes the current connection from a specified group.
 * @param {string} group - The group to remove the connection from.
 * @returns bool
 */
export async function removeConnectionFromGroup(
	group: string,
): Promise<boolean> {
	if (!connectionId) {
		return false
	}

	const url = `notifications/groups/${group}/connections/${connectionId}`

	const response = await signalRNotificationsApi(url, 'DELETE')
	if (!response.ok) {
		return false
	}

	// Remove group if present in the groups array
	if (groups.indexOf(group) !== -1) {
		groups.splice(groups.indexOf(group), 1)
	}

	return true
}

/**
 * Sends a message to all clients within the target group.
 * @param {string} group - The group to send the message to.
 * @param {NotificationMessage} message - The message to send.
 * @param {boolean} [excludeSelf=true] - Whether to exclude the sender from receiving the message.
 * @returns bool
 */
export async function notifyGroup(
	group: string,
	message: NotificationMessage,
	excludeSelf = true,
): Promise<boolean> {
	if (!message.id) {
		message.id = randomId()
	}
	const url = 'notifications/send-notification'
	const response = await signalRNotificationsApi(url, 'POST', {
		message,
		identifier: group,
		target: 'groups',
		...(!excludeSelf && connectionId ? { connectionId } : {}),
	})

	return response.ok
}

/**
 * Sends a message to all clients within the target group.
 * @param {string} userid - The user to send the message to.
 * @param {NotificationMessage} message - The message to send.
 * @returns bool
 */
export async function notifyUser(
	userId: string,
	message: NotificationMessage,
): Promise<boolean> {
	const url = 'notifications/send-notification'
	if (!message.id) {
		message.id = randomId()
	}
	const response = await signalRNotificationsApi(url, 'POST', {
		message,
		identifier: userId,
		target: 'users',
	})

	return response.ok
}

function setConnectionId(newConnectionId: string | null) {
	connectionId = newConnectionId
}

export const notificationsService = {
	composeGroupName,
	addConnectionToGroup,
	removeConnectionFromGroup,
	notifyGroup,
	notifyUser,
	getConnectionId: () => connectionId,
	setConnectionId,
	groups,
}
